import { toast } from "react-toastify";

import { logout, refreshToken } from "@store/actions/user";
import instance from "@store/middleware/api";

import { currentAccessToken, endView, isViewingAsCustomer } from "@utils/viewAs";
import axios, { AxiosError, AxiosResponse } from "axios";

// Prefers this tab's support-view session over the signed-in one. See
// utils/viewAs.ts — a viewing tab and a normal tab differ only here.
const getToken = () => currentAccessToken() ?? "";

/**
 * Writes the app performs on its own — nobody pressed anything.
 *
 * Two kinds, both pointless in a support view. Beacons describing what a
 * visitor did: a support visit is not something the customer did, and sending
 * them inflates that customer's activity and the platform's statistics.
 * Background syncs: the cart reconciling itself, a presence heartbeat, claims
 * being marked as seen — the last of which would erase the customer's own "New"
 * badges just because support looked.
 *
 * They are dropped before they leave the browser, so the server's refusal never
 * arrives and is never reported as though a button had failed. This list only
 * decides what is SILENT; the server refuses every write regardless, so nothing
 * here is load-bearing for the read-only rule.
 */
const UNPROMPTED_WRITES = [
  "/analytics/",
  "/media-info/event",
  "/cart",
  "/presence/heartbeat",
  "/my-claims/viewed",
];

export const handleRequest = (config: any) => {
  const method = (config.method || "get").toLowerCase();
  const isWrite = method !== "get" && method !== "head";
  if (
    isViewingAsCustomer() &&
    isWrite &&
    UNPROMPTED_WRITES.some((p) => (config.url || "").includes(p))
  ) {
    return Promise.reject(new axios.Cancel("not sent while viewing an account"));
  }

  const accessToken = getToken();

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
};

export const handleSuccessResponse = (response: AxiosResponse) => {
  return response;
};

let isRefreshing = false;
let failedQueue: Array<any> = [];
const processQueue = (error: AxiosError | Error | null | unknown, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export const handleErrorResponse = (error: any, store: any) => {
  const { response } = error;
  const token = getToken();

  if (!response) {
    return Promise.reject(error);
  }

  // A support view is read-only, and the refusal comes back from the server on
  // whichever button was pressed. Said once, here, so every blocked control
  // explains itself the same way and none of them looks broken — and so no
  // list of buttons has to be kept in step with what the server refuses.
  if (isViewingAsCustomer() && response.status === 403) {
    // Short, and gone quickly: it answers "why did nothing happen" and has no
    // reason to sit over the page you are trying to look at.
    toast.info("Not available while viewing", {
      toastId: "view_only",
      autoClose: 1200,
      closeButton: false,
    });
    return Promise.reject(response.data);
  }

  // Purchase/download freeze (ToU §17.4): a whitelisting-suspended account is
  // blocked from new purchases + new downloads at the API. Surface ONE clear
  // message anywhere it's hit (deduped by toastId) so no blocked action fails
  // silently or with a generic error, without per-button greying everywhere.
  if (response.status === 403 && response.data?.code === "account_suspended") {
    toast.info(
      response.data?.message ||
        "Your account is paused — restore the missing credit text to continue.",
      { toastId: "account_suspended" },
    );
    return Promise.reject(response.data);
  }

  const errorMsg = {
    status: response.status,
    message: response.data.message || "",
    statusText: response.statusText,
  };

  if (response.status >= 500 && response.status < 600) {
    errorMsg.statusText = "Internal server error";
    errorMsg.message = "Server response with error, please try again later";
  }
  if (response.status === 401) {
    // If the user was never logged in (no access token AND no refresh token),
    // a 401 is the EXPECTED response to my()/getFavorites()/claims-counts etc.
    // that the layout fires on every page load. Dispatching logout here causes
    // spurious side effects (the cart slice's logout.fulfilled extraReducer
    // closes the cart modal — making the cart icon click "do nothing" because
    // the cart opens then immediately re-closes on the next 401). Just reject
    // and let the calling code handle the absent data gracefully.
    // Guest (never logged in): a 401 is the EXPECTED response to my()/claims-
    // counts/etc. that the layout fires on every page load. Just reject and let
    // the caller handle the absent data — don't dispatch logout (spurious side
    // effects) or attempt a refresh.
    if (!token) {
      return Promise.reject(error);
    }
    // A support view has no refresh token by design, so a 401 means its hour is
    // up. Refreshing would fail and then log out — which would clear the
    // ADMIN'S OWN session in localStorage, in a tab that never owned it. Drop
    // the view instead and let the bar say it has ended.
    if (isViewingAsCustomer()) {
      endView();
      window.dispatchEvent(new Event("as-view-ended"));
      return Promise.reject(error);
    }
    if ((response.config.url || "").endsWith("/auth/refresh-session")) {
      // The failing call IS the refresh itself — the HttpOnly refresh cookie is
      // missing/expired, so refreshing is impossible. Log out and reject instead
      // of looping (refresh fails → 401 → recurse → …). `.endsWith` survives any
      // baseURL prefix variation.
      store.dispatch(logout({ accessToken: token, refreshToken: "" }));
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then(() => instance(originalRequest))
        .catch((err) => {
          return Promise.reject(err);
        });
    }
    isRefreshing = true;

    // Hard timeout safety net: if the refresh call neither resolves nor rejects within 20s
    // (slow network, hung server, etc.), force isRefreshing back to false, reject everything
    // queued, and log the user out. Without this guard, a hung refresh leaves isRefreshing
    // pinned to true forever — every subsequent 401 queues up and waits indefinitely, which
    // is exactly the "stuck in loop" P11.12 symptom on dev (user's view: continuous loading,
    // dozens of repeated 401s in the console, never resolves).
    const REFRESH_TIMEOUT_MS = 20_000;
    let timeoutHandle: any = null;
    const finishRefresh = () => {
      isRefreshing = false;
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
        timeoutHandle = null;
      }
    };

    return new Promise((resolve, reject) => {
      timeoutHandle = setTimeout(async () => {
        finishRefresh();
        const timeoutErr = new Error("Token refresh timed out after 20s — logging out");
        processQueue(timeoutErr, null);
        try { await store.dispatch(logout({ accessToken: token, refreshToken: "" })); } catch {}
        reject(timeoutErr);
      }, REFRESH_TIMEOUT_MS);

      store
        .dispatch(refreshToken())
        .then((result: any) => {
          // RTK thunks resolve EVEN ON FAILURE — the failure surfaces as a
          // rejected meta on the result, not as a thrown error. Without this
          // check, a refresh that returned 401 (invalid/expired refresh token)
          // would be treated as "success" → original request retried with the
          // stale token → 401 again → infinite loop. Detect rejection via the
          // standard RTK shape and route through the catch path explicitly.
          if (result?.meta?.requestStatus === "rejected" || result?.error) {
            throw result?.error || new Error("Refresh rejected");
          }
          finishRefresh();
          const updatedAccessToken = getToken();
          processQueue(null, updatedAccessToken);
          resolve(instance(originalRequest));
        })
        .catch(async (err: unknown) => {
          finishRefresh();
          processQueue(err, null);
          try { await store.dispatch(logout({ accessToken: token, refreshToken: "" })); } catch {}
          reject(err);
        });
    });
  }

  return Promise.reject(error.response.data);
};
